import React, { useCallback, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp } from "@fortawesome/pro-regular-svg-icons/faCloudArrowUp";
import { faFile } from "@fortawesome/pro-regular-svg-icons/faFile";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { faDownload } from "@fortawesome/pro-regular-svg-icons/faDownload";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";
import { faRotateRight } from "@fortawesome/pro-regular-svg-icons/faRotateRight";

import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";
import {
  useElementValue,
  useIsReadOnly,
} from "../DocumentView/DocumentContext";
import HiddenElementBanner from "./HiddenElementBanner";
import ArchivedElementBanner from "./ArchivedElementBanner";
import { cn } from "../lib/utils";
import {
  type FileRef,
  fileMatchesAccept,
  useFormResultUpload,
  useResultFile,
} from "./useFormResultUpload";
import { FieldHeader, FieldHelpText } from "./FieldParts";

interface Props {
  element: DocumentElementType;
}

function valueToList(raw: unknown): FileRef[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(isFileRef);
  if (isFileRef(raw)) return [raw];
  return [];
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

function isFileRef(v: unknown): v is FileRef {
  return (
    !!v &&
    typeof v === "object" &&
    typeof (v as { fileId?: unknown }).fileId === "number" &&
    typeof (v as { name?: unknown }).name === "string"
  );
}

const FileUpload: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    accept?: string;
    multiple?: boolean;
  };
  const label = data?.label || "Upload File";
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = !!data?.required;
  const accept = data?.accept || "";
  const multiple = !!data?.multiple;

  const [rawValue, setValue] = useElementValue<FileRef | FileRef[] | null>(
    element.id,
  );
  const files = valueToList(rawValue);
  const { pending, pickFiles, clearPending } = useFormResultUpload();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const isReadOnly = useIsReadOnly();

  const onPick = useCallback(
    async (picked: File[]) => {
      if (picked.length === 0) return;
      const refs = await pickFiles(multiple ? picked : picked.slice(0, 1));
      if (refs.length === 0) return;
      if (multiple) {
        setValue([...files, ...refs]);
      } else {
        setValue(refs[0]);
      }
    },
    [pickFiles, multiple, setValue, files],
  );

  const onDrop = useCallback(
    (ev: React.DragEvent<HTMLDivElement>) => {
      ev.preventDefault();
      setDragActive(false);
      // Honour the same `accept` filter the file picker applies, so a
      // .pdf-only field can't take a .png via drag-and-drop.
      const dropped = Array.from(ev.dataTransfer?.files ?? []).filter((f) =>
        fileMatchesAccept(f, accept),
      );
      if (dropped.length > 0) void onPick(dropped);
    },
    [onPick, accept],
  );

  const handleRemove = useCallback(
    (fileId: number) => {
      if (multiple) {
        setValue(files.filter((f) => f.fileId !== fileId));
      } else {
        setValue(null);
      }
    },
    [files, multiple, setValue],
  );

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      {!isReadOnly && (
        <div
          onDragOver={(ev) => {
            ev.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={(ev) => {
            ev.preventDefault();
            setDragActive(false);
          }}
          onDrop={onDrop}
          className={cn(
            "rounded-[14px] border-[1.6px] border-dashed p-[22px] text-center transition-colors",
            dragActive
              ? "border-[var(--forms-accent,var(--forms-accent-default))] bg-[var(--forms-accent-soft,var(--forms-accent-soft-default))]"
              : "border-[#c6cfdb] bg-[#fafbfc] hover:border-[var(--forms-accent-50,var(--forms-accent-50-default))]",
          )}
        >
          <span
            className="mb-[9px] inline-flex h-[40px] w-[40px] items-center justify-center rounded-[11px] text-[var(--forms-accent,var(--forms-accent-default))]"
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--forms-accent, var(--forms-accent-default)) 10%, #fff)",
            }}
          >
            <FontAwesomeIcon icon={faCloudArrowUp} style={{ fontSize: 20 }} />
          </span>
          <input
            ref={inputRef}
            id={element.id}
            type="file"
            accept={accept}
            multiple={multiple}
            className="hidden"
            onChange={(ev) => {
              const list = ev.target.files;
              if (!list) return;
              const arr = Array.from(list);
              ev.target.value = "";
              void onPick(arr);
            }}
          />
          <div className="text-[13.5px] font-semibold text-[#3a4252]">
            Drag &amp; drop, or{" "}
            <label
              htmlFor={element.id}
              className="cursor-pointer font-bold text-[var(--forms-accent,var(--forms-accent-default))] hover:underline"
            >
              browse
            </label>
          </div>
          {accept && (
            <div className="mt-[3px] text-[11.5px] text-[var(--forms-optional)]">
              {accept}
            </div>
          )}
        </div>
      )}
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((f) => (
            <FileRow
              key={f.fileId}
              fileRef={f}
              onRemove={isReadOnly ? undefined : () => handleRemove(f.fileId)}
            />
          ))}
        </ul>
      )}
      {pending.map((p) => (
        <div
          key={p.uploadId}
          className={`rounded-md border px-3 py-2 text-sm ${
            p.state === "error"
              ? "border-destructive/30 bg-destructive/5"
              : "border-border bg-muted/20"
          }`}
        >
          <div className="flex items-center gap-2">
            {p.state === "error" ? null : (
              <FontAwesomeIcon
                icon={faSpinner}
                className="h-3 w-3 animate-spin text-muted-foreground"
              />
            )}
            <span className="flex-1 truncate">{p.file.name}</span>
            {p.state === "error" ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    clearPending(p.uploadId);
                    void onPick([p.file]);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Retry upload"
                >
                  <FontAwesomeIcon icon={faRotateRight} className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => clearPending(p.uploadId)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Dismiss"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                </button>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">
                {p.progress}%
              </span>
            )}
          </div>
          {p.state === "error" && p.errorMessage && (
            <p className="mt-1 text-xs text-destructive">{p.errorMessage}</p>
          )}
          {p.state !== "error" && (
            <div className="mt-1 h-1 overflow-hidden rounded bg-muted">
              <div
                className="h-full bg-[var(--forms-accent,var(--forms-accent-default))] transition-all"
                style={{ width: `${p.progress}%` }}
              />
            </div>
          )}
        </div>
      ))}
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

interface FileRowProps {
  fileRef: FileRef;
  onRemove?: () => void;
}

const FileRow: React.FC<FileRowProps> = ({ fileRef, onRemove }) => {
  const { file, loading } = useResultFile(fileRef.fileId, { download: true });
  const size = (fileRef as { size?: number }).size;
  return (
    <li className="flex items-center gap-[10px] rounded-[10px] border-[length:var(--forms-input-border-w)] border-[var(--forms-border)] bg-white px-[12px] py-[9px]">
      <span
        className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[8px]"
        style={{ backgroundColor: "#fdeef0", color: "var(--forms-rail-red)" }}
      >
        <FontAwesomeIcon icon={faFile} style={{ fontSize: 15 }} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] font-semibold text-[var(--forms-ink)]">
          {fileRef.name}
        </div>
        {typeof size === "number" && size > 0 && (
          <div className="text-[11px] text-[var(--forms-optional)]">
            {formatBytes(size)}
          </div>
        )}
      </div>
      {file?.url ? (
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer"
          className="flex-none text-[var(--forms-optional)] transition-colors hover:text-[var(--forms-ink)]"
          aria-label={`Download ${fileRef.name}`}
        >
          <FontAwesomeIcon icon={faDownload} style={{ fontSize: 14 }} />
        </a>
      ) : loading ? (
        <FontAwesomeIcon
          icon={faSpinner}
          className="h-3 w-3 flex-none animate-spin text-[var(--forms-optional)]"
        />
      ) : null}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="flex-none text-[var(--forms-optional)] transition-colors hover:text-[var(--forms-rail-red)]"
          aria-label={`Remove ${fileRef.name}`}
        >
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 15 }} />
        </button>
      )}
    </li>
  );
};

export default FileUpload;
